import { useMutation, useQueryClient } from '@tanstack/react-query';

import { customerSaleQueryOptions } from '@/api/customer/customerSaleQuery';
import type { CustomerSaleResponse } from '@/api/customer/customerSaleQuery/types';
import { customerStateQueryOptions } from '@/api/customer/customerStateQuery';
import { useToast } from '@/components/_shared/Toaster';
import apiClient from '@/constants/http';
import { APPOINTMENT_LOOKUP_QUERY_KEY } from '@/hooks/queries/appointment/useAppointmentLookupQuery';
import { useErrorTranslations } from '@/hooks/useErrorTranslations';
import { Logger } from '@/utils/logging';
import {
  ANY_KEY_SEGMENT,
  invalidateQueriesByKeyPattern,
} from '@/utils/queryInvalidation';
import { Tracker } from '@/utils/tracking';

import type {
  CancelOrderMutationContext,
  CancelOrderParams,
  UseCancelOrderMutationOptions,
} from './types';

const cancelAppointment = async (
  appointmentId: string,
  cancelGroup: boolean,
): Promise<unknown> => {
  try {
    const response = await apiClient.post(
      `/v1/cancel-appointment/${appointmentId}`,
      { cancelGroup },
      { timeout: 30000 },
    );

    return response.data;
  } catch (error) {
    Logger.error('Error canceling appointment', { error, appointmentId });
    throw error;
  }
};

const cancelOrder = async ({
  saleOrderId,
  appointmentIds,
}: CancelOrderParams): Promise<void> => {
  const [firstAppointmentId] = appointmentIds;

  if (!firstAppointmentId) {
    return;
  }

  try {
    await cancelAppointment(firstAppointmentId, appointmentIds.length > 1);
  } catch (error) {
    Logger.error('Error canceling order', {
      error,
      saleOrderId,
      appointmentIds,
    });
    throw error;
  }
};

/**
 * React Query mutation hook for canceling an order (all appointments in a sale order)
 *
 * @param options - Mutation configuration options
 * @returns Mutation result with updated sale data
 */
export const useCancelOrderMutation = (
  options?: UseCancelOrderMutationOptions,
) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useErrorTranslations('appointment');

  return useMutation<
    void,
    Error,
    CancelOrderParams,
    CancelOrderMutationContext
  >({
    mutationFn: cancelOrder,
    onMutate: async (variables) => {
      const saleQueryKey = customerSaleQueryOptions(
        variables.saleOrderId,
      ).queryKey;

      // Cancel any outgoing refetches to prevent race conditions during mutation
      await queryClient.cancelQueries({ queryKey: saleQueryKey });

      // Get the current sale data to extract customerId for later use in onSettled
      const customerSale =
        queryClient.getQueryData<CustomerSaleResponse>(saleQueryKey);

      // Get customerId from sale data for customer state query invalidation
      const customerId = customerSale?.items[0]?.customerId;

      if (customerId) {
        const stateQueryKey = customerStateQueryOptions(customerId).queryKey;

        // Cancel any outgoing refetches for customer state
        await queryClient.cancelQueries({ queryKey: stateQueryKey });
      }

      // NOTE: We intentionally do NOT optimistically update the sale state to 'canceled'
      // here. Doing so would cause the UI to show the cancellation success screen
      // before the API call actually completes. Instead, we wait for the mutation
      // to succeed, then the refetch (via onSettled) will update the data with
      // the actual canceled state from the server.

      // Return context with customerId for use in onSettled
      return {
        customerId,
      } as CancelOrderMutationContext;
    },
    // Handle mutation errors - no rollback needed since we don't do optimistic updates
    onError: (error, variables, context, mutationContext) => {
      Logger.error('Error canceling order', { error, variables });

      Tracker.track('appointment_cancel_failed', {
        saleOrderId: variables.saleOrderId,
        appointmentCount: variables.appointmentIds.length.toString(),
        error: String(error),
      });

      const message =
        error instanceof Error || 'message' in error
          ? error.message
          : t('unknownError');

      toast({
        variant: 'error',
        message: t('cancelFailed'),
        description: message,
      });

      options?.onError?.(error, variables, context, mutationContext);
    },
    // Always refetch after error or success to ensure consistency
    onSettled: (_data, _error, variables) => {
      // Invalidate customer sale query
      queryClient.invalidateQueries({
        queryKey: customerSaleQueryOptions(variables.saleOrderId).queryKey,
      });

      // Invalidate appointment-lookup to refresh home screen
      queryClient.invalidateQueries({
        queryKey: APPOINTMENT_LOOKUP_QUERY_KEY,
      });

      // Match by key shape rather than the customerId resolved in onMutate -
      // that resolution can come back empty (e.g. an empty sale snapshot),
      // which would otherwise silently skip this invalidation (SQR-15876).
      invalidateQueriesByKeyPattern(
        queryClient,
        customerStateQueryOptions(ANY_KEY_SEGMENT).queryKey,
      );
    },
    onSuccess: (data, variables, context, mutationContext) => {
      Tracker.track('appointment_canceled', {
        saleOrderId: variables.saleOrderId,
        appointmentCount: variables.appointmentIds.length.toString(),
      });

      options?.onSuccess?.(data, variables, context, mutationContext);
    },
    retry: false,
    ...options,
  });
};
